import ExcelJS from 'exceljs'

const COLORS = {
  headerDark: 'FF404040',
  white: 'FFFFFFFF',
  borderLight: 'FFE5E5E5',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  submitted: 'Enviada',
  reviewed: 'Revisada',
  completed: 'Concluida',
}

const TYPE_LABELS: Record<string, string> = {
  self: 'Autoavaliacao',
  manager: 'Gestor',
  peer: 'Par',
  subordinate: 'Subordinado',
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

interface ExportParams {
  evaluations: any[]
  statusFilter: string
}

export async function exportAvaliacoesToExcel({
  evaluations,
  statusFilter,
}: ExportParams): Promise<void> {
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'Monutrack'
  workbook.created = new Date()
  workbook.modified = new Date()

  const worksheet = workbook.addWorksheet('Todas as Avaliacoes', {
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true },
  })

  // Column widths
  worksheet.columns = [
    { width: 25 }, // Colaborador
    { width: 25 }, // Avaliador
    { width: 16 }, // Tipo Avaliador
    { width: 14 }, // Status
    { width: 12 }, // Nota Geral
    { width: 14 }, // Periodo Inicio
    { width: 14 }, // Periodo Fim
    { width: 14 }, // Enviado Em
    { width: 14 }, // Revisado Em
    { width: 14 }, // Atualizado Em
    { width: 20 }, // Item ID
    { width: 10 }, // Nota Item
    { width: 10 }, // Pulado
    { width: 30 }, // Motivo
    { width: 35 }, // Comentarios
    { width: 35 }, // Evidencia
    { width: 35 }, // Plano Desenvolvimento
  ]

  // Title row
  const titleRow = worksheet.addRow(['Todas as Avaliacoes — Exportacao'])
  titleRow.font = { size: 14, bold: true }
  titleRow.alignment = { vertical: 'middle', horizontal: 'center' }
  worksheet.mergeCells(1, 1, 1, 17)

  // Info row
  const infoRow = worksheet.addRow([
    `Gerado em ${new Date().toLocaleString('pt-BR')} | Filtro: ${statusFilter === 'all' ? 'Todos' : STATUS_LABELS[statusFilter] || statusFilter} | Total: ${evaluations.length} avaliacoes`,
  ])
  infoRow.font = { size: 10 }
  infoRow.alignment = { vertical: 'middle', horizontal: 'center' }
  worksheet.mergeCells(2, 1, 2, 17)

  // Empty row
  worksheet.addRow([])

  // Header row
  const headers = [
    'Colaborador',
    'Avaliador',
    'Tipo Avaliador',
    'Status',
    'Nota Geral',
    'Periodo Inicio',
    'Periodo Fim',
    'Enviado Em',
    'Revisado Em',
    'Atualizado Em',
    'Item ID',
    'Nota Item',
    'Pulado',
    'Motivo (Nao se Aplica)',
    'Comentarios',
    'Evidencia',
    'Plano Desenvolvimento',
  ]
  const headerRow = worksheet.addRow(headers)
  headerRow.font = { bold: true, color: { argb: COLORS.white } }
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: COLORS.headerDark },
  }
  headerRow.alignment = { vertical: 'middle', horizontal: 'center' }
  headerRow.height = 25

  // Freeze header
  worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }]

  // Data rows — one row per score, or one row per evaluation if no scores
  for (const evaluation of evaluations) {
    const employeeName = evaluation.employeeName || ''
    const evaluatorName = evaluation.evaluatorName || ''
    const typeLabel = TYPE_LABELS[evaluation.evaluatorType] || evaluation.evaluatorType || ''
    const statusLabel = STATUS_LABELS[evaluation.status] || evaluation.status || ''
    const overallScore = evaluation.overallScore != null ? Number(Number(evaluation.overallScore).toFixed(1)) : null
    const periodStart = formatDate(evaluation.evaluationPeriod?.startDate)
    const periodEnd = formatDate(evaluation.evaluationPeriod?.endDate)
    const submittedAt = formatDate(evaluation.submittedAt)
    const reviewedAt = formatDate(evaluation.reviewedAt)
    const updatedAt = formatDate(evaluation.updatedAt)

    const scores = evaluation.scores || []

    if (scores.length === 0) {
      worksheet.addRow([
        employeeName, evaluatorName, typeLabel, statusLabel, overallScore,
        periodStart, periodEnd, submittedAt, reviewedAt, updatedAt,
        '', null, '', '', '', '', '',
      ])
    } else {
      for (const score of scores) {
        worksheet.addRow([
          employeeName, evaluatorName, typeLabel, statusLabel, overallScore,
          periodStart, periodEnd, submittedAt, reviewedAt, updatedAt,
          score.itemId || '',
          score.score ?? null,
          score.skipped ? 'Sim' : 'Nao',
          score.skipReason || '',
          score.comments || '',
          score.evidence || '',
          score.developmentPlan || '',
        ])
      }
    }
  }

  // Apply borders
  const lastRow = worksheet.rowCount
  for (let i = 4; i <= lastRow; i++) {
    const row = worksheet.getRow(i)
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = {
        top: { style: 'thin', color: { argb: COLORS.borderLight } },
        left: { style: 'thin', color: { argb: COLORS.borderLight } },
        bottom: { style: 'thin', color: { argb: COLORS.borderLight } },
        right: { style: 'thin', color: { argb: COLORS.borderLight } },
      }
    })
  }

  // Download
  const date = new Date().toISOString().split('T')[0]
  const filterSuffix = statusFilter !== 'all' ? `-${statusFilter}` : ''
  const fileName = `avaliacoes-individuais${filterSuffix}-${date}.xlsx`

  const buffer = await workbook.xlsx.writeBuffer()
  downloadFile(buffer as ArrayBuffer, fileName)
}

function downloadFile(buffer: ArrayBuffer, fileName: string) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
